import { useEffect, useState } from 'react';
import {
  listPersonalAccessTokens,
  createPersonalAccessToken,
  deletePersonalAccessToken,
  PersonalAccessToken,
  PatCreateRequest,
  PatCreateResponse,
} from '@/services/identity/pat.service';
import { useBaseViewModel } from '@/common/bases/base-viewmodel';
import { logEvent, logError } from '@/appInsights';

export interface UsePersonalAccessTokensResult {
  tokens: PersonalAccessToken[];
  total: number;
  loading: boolean;
  error?: Error;
  refresh: () => Promise<void>;
  create: (payload: PatCreateRequest) => Promise<PatCreateResponse>;
  delete: (tokenId: string) => Promise<void>;
}

export const usePersonalAccessTokens = (): UsePersonalAccessTokensResult => {
  const { catchAction, loading, error } = useBaseViewModel();
  const [tokens, setTokens] = useState<PersonalAccessToken[]>([]);
  const [total, setTotal] = useState(0);

  const loadTokens = async () => {
    await catchAction(async () => {
      const response = await listPersonalAccessTokens();
      setTokens(response.tokens);
      setTotal(response.total);
      logEvent('PAT_List_Loaded', { tokenCount: response.tokens.length });
    });
  };

  const createToken = async (payload: PatCreateRequest): Promise<PatCreateResponse> => {
    let result: PatCreateResponse | null = null;
    await catchAction(async () => {
      result = await createPersonalAccessToken(payload);
      logEvent('PAT_Created', { tokenName: payload.name });
      await loadTokens();
    });
    if (!result) {
      const error = new Error('Failed to create token');
      logError(error, { action: 'create_token' });
      throw error;
    }
    return result;
  };

  const deleteToken = async (tokenId: string) => {
    await catchAction(async () => {
      await deletePersonalAccessToken(tokenId);
      logEvent('PAT_Deleted', { tokenId });
      await loadTokens();
    });
  };

  useEffect(() => {
    loadTokens();
  }, []);

  return {
    tokens,
    total,
    loading,
    error,
    refresh: loadTokens,
    create: createToken,
    delete: deleteToken,
  };
};
